import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

interface AnimeSeries {
  id: string;
  series_name: string;
  cover_image_url: string | null;
  description: string | null;
}

export default function SeriesPage() {
  const { name } = useParams<{ name: string }>();
  const [characters, setCharacters] = useState<AnimeCharacter[]>([]);
  const [seriesInfo, setSeriesInfo] = useState<AnimeSeries | null>(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    if (name) {
      loadSeriesData();
    }
  }, [name]);

  const loadSeriesData = async () => {
    const decodedName = decodeURIComponent(name || '');
    
    // Load series info from anime_series table
    const { data: seriesData } = await supabase
      .from('anime_series')
      .select('*')
      .eq('series_name', decodedName)
      .maybeSingle();

    if (seriesData) {
      setSeriesInfo(seriesData);
    }

    // Load characters
    const { data } = await supabase
      .from('anime_characters')
      .select('*')
      .eq('anime_name', decodedName)
      .order('character_name', { ascending: true });

    if (data) {
      // Additional client-side sorting to ensure proper alphabetical order
      const sortedData = data.sort((a, b) => 
        a.character_name.localeCompare(b.character_name, 'en', { 
          numeric: true, 
          sensitivity: 'base' 
        })
      );
      setCharacters(sortedData);
    }
    setLoading(false);
  };

  const scrollToCharacters = () => {
    const element = document.getElementById('character-list');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  const decodedName = decodeURIComponent(name || '');

  // Helper function to create SEO-friendly slug
  const createCharacterSlug = (character: AnimeCharacter) => {
    const animeName = character.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const charName = character.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animeName}-${charName}-${character.id}`;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${decodedName} Characters`,
    description: `Browse and rate all characters from ${decodedName}`,
    numberOfItems: characters.length,
    itemListElement: characters.map((character, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Person',
        name: character.character_name,
        image: character.image_url,
        url: `${siteUrl}/character/${createCharacterSlug(character)}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: character.average_rating.toFixed(1),
          bestRating: '10',
          worstRating: '1',
          ratingCount: character.total_ratings
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title={`${decodedName} Characters - Rate & Review All Characters`}
        description={`Browse and rate all ${characters.length} characters from ${decodedName}. See ratings, reviews, and join the anime community in voting for the hottest characters.`}
        keywords={`${decodedName}, ${decodedName} characters, rate ${decodedName}, ${decodedName} character rating, anime characters, ${decodedName} reviews`}
        canonical={`${siteUrl}/series/${encodeURIComponent(decodedName)}`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Link
              to="/series"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-pink-500 transition-colors cursor-pointer mb-4"
            >
              <i className="ri-arrow-left-line"></i>
              Back to All Anime Series
            </Link>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-2">
              {decodedName}
            </h1>
          </div>

          {/* Skip to Characters Link */}
          {characters.length > 0 && (
            <div className="mb-6">
              <button
                onClick={scrollToCharacters}
                className="inline-flex items-center gap-2 text-sm font-semibold text-pink-500 hover:text-purple-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-down-line"></i>
                Skip to Characters
              </button>
            </div>
          )}

          {/* Series Cover Image and Description Section */}
          {seriesInfo && (seriesInfo.cover_image_url || seriesInfo.description) && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Cover Image */}
                {seriesInfo.cover_image_url ? (
                  <div className="flex-shrink-0">
                    <div className="w-full md:w-64 aspect-[2/3] rounded-xl overflow-hidden shadow-lg">
                      <img
                        src={seriesInfo.cover_image_url}
                        alt={`${decodedName} cover`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex-shrink-0">
                    <div className="w-full md:w-64 aspect-[2/3] rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                      <i className="ri-film-line text-white text-6xl"></i>
                    </div>
                  </div>
                )}

                {/* Description */}
                {seriesInfo.description && (
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">About {decodedName}</h2>
                    <div 
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed series-description-content [&_p]:mb-4 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: seriesInfo.description }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Character Count */}
          <div className="mb-6" id="character-list">
            <p className="text-lg text-gray-600">
              {characters.length} {characters.length === 1 ? 'character' : 'characters'} available
            </p>
          </div>

          {characters.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {characters.map((character) => (
                <Link
                  key={character.id}
                  to={`/character/${createCharacterSlug(character)}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img
                      src={character.image_url}
                      alt={`${character.character_name} - ${character.anime_name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2">
                      {character.character_name}
                    </h3>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        <i className="ri-heart-fill text-pink-500 text-base w-4 h-4 flex items-center justify-center"></i>
                        <span className="font-semibold text-gray-900">
                          {character.average_rating.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-gray-500">
                        {character.total_ratings} {character.total_ratings === 1 ? 'rating' : 'ratings'}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full ${
                        character.gender === 'girl' ? 'bg-pink-100' : 
                        character.gender === 'boy' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        <i className={`ri-${
                          character.gender === 'girl' ? 'women' : 
                          character.gender === 'boy' ? 'men' : 'genderless'
                        }-line text-sm ${
                          character.gender === 'girl' ? 'text-pink-500' : 
                          character.gender === 'boy' ? 'text-blue-500' : 'text-purple-500'
                        }`}></i>
                      </div>
                      <span className="text-xs text-gray-600 capitalize">
                        {character.gender === 'boy' ? 'Male' : character.gender === 'girl' ? 'Female' : 'Neutral'}
                      </span>
                    </div>

                    <div className="mt-4 flex items-center justify-center gap-2 py-2 px-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                      <span>Rate Character</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="ri-emotion-sad-line text-4xl text-gray-400"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Characters Found</h2>
              <p className="text-gray-600 mb-6">
                There are no characters available for this anime series yet.
              </p>
              <Link
                to="/rate"
                className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Browse All Characters
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
      <style>{`
        .series-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .series-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .series-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .series-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
